// src/components/auth/AuthSplitLayout.jsx
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import "./AuthSplitLayout.css";
import { getRedirectFromSearch, withRedirect } from "../../utils/authRedirect";

// Foto: client/public/images/register_img.jpg -> URL pubblico
const HERO_IMG = "/images/register_img.jpg";

const STIRUP_SITE_URL = "https://www.stirup.it";

// Il logo porta al sito esterno StirUp. Su Capacitor NON deve aprirsi nella
// webview dell'app (l'utente resterebbe intrappolato): usiamo Browser.open per
// aprirlo nel browser di sistema. Sul web è una normale apertura in nuova scheda.
async function openStirUpSite() {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url: STIRUP_SITE_URL });
    return;
  }
  window.open(STIRUP_SITE_URL, "_blank", "noopener,noreferrer");
}

/**
 * Layout a due colonne per le pagine di autenticazione (login, register
 * cliente/fornitore).
 * Props:
 *  - heroTitle:      titolo grande del pannello sinistro
 *  - heroSubtitle:   sottotitolo del pannello sinistro
 *  - cardTitle:      titolo della card del form (destra)
 *  - cardSubtitle:   sottotitolo della card del form (destra)
 *  - showLoginLink:  mostra in alto a destra il link "← Accedi" verso /login
 *                     (usato da register/supplier-register, non da login)
 *  - hideCardSubtitleOnMobile: nasconde cardSubtitle solo su mobile/app
 *                     (usato dal login, per non occupare spazio su schermo stretto)
 *  - hideHeroTextOnMobile: nasconde heroTitle+heroSubtitle (pannello sinistro)
 *                     solo su mobile/app, lasciando foto e decori (usato dal
 *                     login; register/supplier-register non lo passano, quindi
 *                     restano invariati)
 *  - children:       il form vero e proprio
 */
export default function AuthSplitLayout({
  heroTitle,
  heroSubtitle,
  cardTitle,
  cardSubtitle,
  showLoginLink = false,
  hideCardSubtitleOnMobile = false,
  hideHeroTextOnMobile = false,
  children,
}) {
  // "← Accedi" conserva la pagina richiesta prima del login (?redirect=)
  const location = useLocation();
  const loginTo = withRedirect("/login", getRedirectFromSearch(location.search));

  return (
    <div className="asl">
      {/* ===== PANNELLO SINISTRO (hero) ===== */}
      <section className="asl-hero">
        <div className="asl-brand">
          <button
            type="button"
            className="asl-brand__logo-btn"
            onClick={openStirUpSite}
            aria-label="Vai al sito StirUp"
          >
            <img
              src="/logo.png"
              alt="StirUp logo"
              className="asl-brand__logo"
              width={140}
              height={44}
            />
          </button>
        </div>

        <div className="asl-hero__media">
          <span className="asl-deco asl-deco--dots tl" aria-hidden />
          <span className="asl-deco asl-deco--dots br" aria-hidden />
          <span className="asl-deco asl-deco--star s1" aria-hidden>&#10022;</span>
          <span className="asl-deco asl-deco--cross c1" aria-hidden>&#10005;</span>
          <span className="asl-deco asl-deco--ring r1" aria-hidden />
          <span className="asl-deco asl-deco--ring r2" aria-hidden />
          <img src={HERO_IMG} alt="" className="asl-hero__img" />
        </div>

        <div
          className={
            "asl-hero__text" +
            (hideHeroTextOnMobile ? " asl-hero__text--mobile-hide" : "")
          }
        >
          <h1>{heroTitle}</h1>
          <div className="asl-hero__accent" />
          <p>{heroSubtitle}</p>
        </div>
      </section>

      {/* ===== PANNELLO DESTRO (form) ===== */}
      <section className="asl-formcol">
        <div className="asl-card">
          {showLoginLink && (
            <div className="asl-back">
              <Link to={loginTo} className="asl-link">
                &#8592; Accedi
              </Link>
            </div>
          )}

          <h2 className="asl-card__title">{cardTitle}</h2>
          {cardSubtitle && (
            <p
              className={
                "asl-card__sub" +
                (hideCardSubtitleOnMobile ? " asl-card__sub--mobile-hide" : "")
              }
            >
              {cardSubtitle}
            </p>
          )}

          <div className="asl-card__body">{children}</div>
          <Link to="/privacy/preferenze" className="asl-link privacy-preferences-access">Privacy e preferenze</Link>
        </div>
      </section>
    </div>
  );
}
